import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { Users, Star, Plane, Save, TrendingUp, Mail } from "lucide-react";

function StatCard({ label, value, sublabel, testid }) {
    return (
        <div className="bg-surface border border-ink/10 rounded-2xl p-5" data-testid={testid}>
            <div className="text-xs text-ink-muted uppercase tracking-wider">{label}</div>
            <div className="font-serif text-3xl mt-2" data-testid={`${testid}-value`}>{value}</div>
            {sublabel && <div className="text-xs text-ink-muted mt-1">{sublabel}</div>}
        </div>
    );
}

function Sparkline({ points, color = "#1a1a1a", testid }) {
    if (!points || points.length === 0) return <div className="h-12 text-xs text-ink-muted">No data</div>;
    const max = Math.max(...points.map((p) => p.count), 1);
    const w = 280, h = 60, pad = 4;
    const stepX = (w - pad * 2) / Math.max(points.length - 1, 1);
    const path = points.map((p, i) => {
        const x = pad + i * stepX;
        const y = h - pad - (p.count / max) * (h - pad * 2);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return (
        <svg width={w} height={h} className="text-ink" data-testid={testid}>
            <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
    );
}

function RankList({ items, valueKey = "count", labelKey, testid }) {
    if (!items || items.length === 0) return <div className="text-xs text-ink-muted py-2">No data yet.</div>;
    const max = Math.max(...items.map((i) => i[valueKey]), 1);
    return (
        <ul className="space-y-2" data-testid={testid}>
            {items.slice(0, 8).map((it, i) => (
                <li key={(it[labelKey] || it.destination_id || it.country || it.city || it.planet) + i} className="flex items-center gap-3 text-sm">
                    <span className="flex-1 truncate">{it[labelKey] || it.destination_id || it.country || it.city || it.planet || "—"}</span>
                    <span className="w-24 h-1 rounded-full bg-ink/10 relative overflow-hidden">
                        <span className="absolute inset-y-0 left-0 bg-ink/60 rounded-full" style={{ width: `${(it[valueKey] / max) * 100}%` }} />
                    </span>
                    <span className="w-8 text-right tabular-nums text-ink-muted text-xs">{it[valueKey]}</span>
                </li>
            ))}
        </ul>
    );
}

export default function AdminOverview() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancel = false;
        api.get("/admin/overview")
            .then((r) => { if (!cancel) setData(r.data); })
            .catch((e) => { if (!cancel) setError(e.response?.data?.detail || e.message); });
        return () => { cancel = true; };
    }, []);

    if (error) return <AdminLayout title="Overview"><div className="text-sm text-red-500" data-testid="admin-overview-error">{error}</div></AdminLayout>;
    if (!data) return <AdminLayout title="Overview"><div className="text-ink-muted">Loading…</div></AdminLayout>;

    const t = data.totals;
    return (
        <AdminLayout title="Overview" subtitle="Application performance, user growth, and engagement metrics.">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-overview-totals">
                <StatCard testid="stat-total-users" label="Total Users" value={t.users} />
                <StatCard testid="stat-new-today" label="New Today" value={t.new_today} />
                <StatCard testid="stat-new-7d" label="New · 7 Days" value={t.new_7d} />
                <StatCard testid="stat-new-30d" label="New · 30 Days" value={t.new_30d} />
                <StatCard testid="stat-active-24h" label="Active · 24h" value={t.active_24h} />
                <StatCard testid="stat-active-7d" label="Active · 7 Days" value={t.active_7d} />
                <StatCard testid="stat-total-recs" label="Recommendations" value={t.total_recommendations} sublabel={`avg ${t.avg_recommendations_per_user}/user`} />
                <StatCard testid="stat-flight-clicks" label="Flight Clicks" value={t.total_flight_clicks} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Users strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Signups (30d)</span></div>
                    <Sparkline points={data.trends.signups} testid="trend-signups" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Star strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Recommendations (30d)</span></div>
                    <Sparkline points={data.trends.recommendations} testid="trend-recs" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Plane strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Flight Searches (30d)</span></div>
                    <Sparkline points={data.trends.flight_searches} testid="trend-flights" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Save strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Most Saved</span></div>
                    <RankList items={data.most_saved} labelKey="destination_id" testid="rank-saved" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Star strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Most Recommended</span></div>
                    <RankList items={data.most_recommended} labelKey="destination_id" testid="rank-recommended" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3"><Plane strokeWidth={1.5} className="w-4 h-4" /><span className="sp-overline">Most Flight-Searched</span></div>
                    <RankList items={data.most_flight_searched} labelKey="destination_id" testid="rank-flights" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="sp-overline mb-3">Top Countries</div>
                    <RankList items={data.top_countries} labelKey="country" testid="rank-countries" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="sp-overline mb-3">Top Cities</div>
                    <RankList items={data.top_cities} labelKey="city" testid="rank-cities" />
                </div>
                <div className="bg-surface border border-ink/10 rounded-2xl p-5">
                    <div className="sp-overline mb-3">Popular Planets</div>
                    <RankList items={data.top_planets} labelKey="planet" testid="rank-planets" />
                </div>
            </div>

            <EmailWidget />
        </AdminLayout>
    );
}

function EmailWidget() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/email-stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    if (!stats) return null;
    return (
        <div className="bg-surface border border-ink/10 rounded-2xl p-5" data-testid="admin-email-stats">
            <div className="flex items-center gap-2 mb-4">
                <Mail strokeWidth={1.5} className="w-4 h-4" />
                <span className="sp-overline">Email Performance</span>
                {!stats.configured && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-ink-muted">Resend not yet configured</span>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div><div className="text-xs text-ink-muted">Welcome</div><div className="font-serif text-2xl">{stats.welcome_emails_sent || "—"}</div></div>
                <div><div className="text-xs text-ink-muted">Travel Alerts</div><div className="font-serif text-2xl">{stats.travel_alerts_sent || "—"}</div></div>
                <div><div className="text-xs text-ink-muted">Open Rate</div><div className="font-serif text-2xl">{stats.open_rate ?? "—"}</div></div>
                <div><div className="text-xs text-ink-muted">Click Rate</div><div className="font-serif text-2xl">{stats.click_rate ?? "—"}</div></div>
                <div><div className="text-xs text-ink-muted">Failures</div><div className="font-serif text-2xl">{stats.delivery_failures || 0}</div></div>
            </div>
        </div>
    );
}
